import React, { useState, useEffect } from 'react';
import { MessageCircle, RefreshCw } from 'lucide-react';
import apiService from '../services/apiService';
import { useI18n } from '../utils/i18n';
import './TeamsMessageList.css';

const TeamsView = () => {
  const { t, getLocale } = useI18n();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadTeamsMessages();
  }, []);

  const loadTeamsMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load current user info first if not loaded
      if (!currentUser) {
        const userResponse = await apiService.getCurrentUser();
        setCurrentUser(userResponse);
      }
      
      const response = await apiService.getTeamsMessages(30, false);
      let messageList = response.value || [];
      
      // Additional client-side filtering to remove messages from current user
      if (currentUser) {
        messageList = messageList.filter(message => {
          const senderName = message.from?.user?.displayName;
          const currentUserName = currentUser.displayName;
          
          // Filter out messages from current user
          if (senderName && currentUserName && senderName === currentUserName) {
            console.log(`Filtering out message from current user: ${senderName}`);
            return false;
          }
          
          return true;
        });
      }
      
      setMessages(messageList);
    } catch (err) {
      console.error('Error loading Teams messages:', err);
      setError(err.message || t('teamsView.loadError', 'Fehler beim Laden der Teams-Nachrichten'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    // Kehrwoche A (#33): vorher stand hier ein require()-Hook-Aufruf in einer normalen Funktion
    // (Hook-Regelverstoß, lief nur dank try/catch) — jetzt t()/getLocale() aus dem Component-Scope.
    const locale = getLocale();
    const rel = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (diffMins < 1) return t('teamsView.justNow', 'gerade eben');
    if (diffMins < 60) return rel.format(-diffMins, 'minute');
    if (diffHours < 24) return rel.format(-diffHours, 'hour');
    if (diffDays < 7) return rel.format(-diffDays, 'day');

    return date.toLocaleDateString(locale, {
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMessageContent = (message) => {
    if (!message.body?.content) return t('teamsView.noContent', 'Nachricht ohne Inhalt');
    
    // Remove HTML tags for display
    const content = message.body.content.replace(/<[^>]*>/g, '');
    
    return content;
  };

  const getSenderName = (message) => {
    if (message.from?.user?.displayName) {
      return message.from.user.displayName;
    }
    return t('teamsView.unknownSender', 'Unbekannter Absender');
  };

  const handleRefresh = async () => {
    await loadTeamsMessages();
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <RefreshCw className="animate-spin" size={32} />
          <p>{t('teamsView.loading', 'Teams-Nachrichten werden geladen...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <div className="error-icon">⚠️</div>
          <div className="error-message">
            <h4>{t('teamsView.loadError', 'Fehler beim Laden der Teams-Nachrichten')}</h4>
            <p>{error}</p>
            <button onClick={loadTeamsMessages} className="btn btn-primary">
              {t('dashboard.retry', 'Erneut versuchen')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="teams-view">
        <div className="teams-header">
          <h1>
            <MessageCircle size={24} />
            {t('teamsView.title', 'Teams-Nachrichten')}
          </h1>
          <button onClick={handleRefresh} className="btn btn-primary">
            <RefreshCw size={16} />
            {t('admin.refresh', 'Aktualisieren')}
          </button>
        </div>

        {messages.length === 0 ? (
          <div className="teams-empty">
            <div className="empty-icon">💬</div>
            <h3>{t('teamsView.empty', 'Keine Teams-Nachrichten')}</h3>
            <p>{t('teamsView.emptyHint', 'Es wurden keine aktuellen Teams-Nachrichten gefunden.')}</p>
          </div>
        ) : (
          <div className="teams-messages-full">
            <div className="messages-count">
              {t('teamsView.count', '{{count}} Nachrichten (letzte 30)', { count: messages.length })}
            </div>
            
            {messages.map((message, index) => (
              <div key={message.id || index} className="teams-message-full">
                <div className="message-header">
                  <div className="sender-info">
                    <span className="sender-name">{getSenderName(message)}</span>
                    <span className="chat-info">
                      {message.chatTopic && message.chatTopic !== 'Direct Chat' && (
                        <span className="chat-topic">{t('teamsView.inChat', 'in {{topic}}', { topic: message.chatTopic })}</span>
                      )}
                    </span>
                  </div>
                  <div className="message-time">
                    {formatDate(message.createdDateTime)}
                  </div>
                </div>
                
                <div className="message-content-full">
                  {formatMessageContent(message)}
                </div>
                
                <div className="message-footer">
                  <span className="chat-type">
                    {message.chatType === 'oneOnOne' ? 'Direktnachricht' : 'Gruppenchat'}
                  </span>
                  {message.importance && message.importance === 'high' && (
                    <span className="importance-indicator">❗</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamsView; 